import React, { useEffect, useMemo, useRef } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@core/design-system/button';
import Link from 'next/link';
import { useGeneralSearch } from '../../hooks/use-general-search.hook';
import { StyledListItem } from '../../styles';
import { useTheme } from '@mui/material';
import { RetornoAdsTooltip } from '@mais-retorno-front/components/retorno-ads/components/tooltip';
import { IGenericSearchFormattedAsset } from '@mais-retorno-front/core-business/useCases/search-asset/interface';

interface IAssetInfo {
  slug: string;
  displayName: string;
  value: string | number;
}

interface IOption {
  isFeatured?: boolean;
  asset: IGenericSearchFormattedAsset;
  infos: IAssetInfo[];
  comparisonMode: boolean;
  handleCloseDialog: () => void;
  clearInput: () => void;
  linkProps?: Partial<HTMLAnchorElement>;
}

export const Option = ({
  asset,
  infos,
  comparisonMode,
  handleCloseDialog,
  clearInput,
  isFeatured = false,
  linkProps = {},
}: IOption) => {
  const theme = useTheme();
  const listItemRef = useRef<HTMLAnchorElement>(null);
  const { addNewAsset, removeAsset, assets } = useGeneralSearch();
  const handleMouseEnter = (event: MouseEvent) => {
    listItemRef.current.focus();
  };

  // STYLED CANNOT RECEIVE TRUE, SO IF TRUE SEND UNDEFINED ELSE SEND FALSE
  const assetEnable = useMemo<boolean>(() => {
    if (asset.enable || asset.enable === undefined || asset.enable === null)
      return undefined;

    return false;
  }, []);

  const handleClickLink = (e: React.MouseEvent) => {
    handleCloseDialog();
    clearInput();
  };

  const handleAssetsToComparison = (e: React.MouseEvent) => {
    if (assetEnable === false) return;

    assets.find((item) => item.identifier === asset.identifier)
      ? removeAsset(asset)
      : addNewAsset(asset);
    handleCloseDialog();
    clearInput();
  };

  useEffect(() => {
    listItemRef?.current?.addEventListener('mouseenter', handleMouseEnter);

    return () =>
      listItemRef?.current?.removeEventListener('mouseenter', handleMouseEnter);
  }, [handleMouseEnter]);

  const renderContent = () => {
    return (
      <Box display="flex" flexDirection="column" width="100%" gap={1}>
        <Stack
          flexDirection={{ xs: 'column', sm: 'row' }}
          rowGap={1}
          columnGap={2}
        >
          <Chip
            label={asset.type}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ width: 'fit-content' }}
          />
          <Typography
            fontWeight={700}
            style={{ textTransform: 'uppercase' }}
            color={(theme) =>
              isFeatured ? theme.palette.secondary.dark : 'text.primary'
            }
          >
            {asset.nicename}
          </Typography>

          {isFeatured && (
            <Box sx={{ position: 'absolute', top: 3, right: 3 }}>
              <Typography
                variant="body2"
                color={(theme) => theme.palette.secondary.dark}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                Patrocinado
                <RetornoAdsTooltip size={14} />
              </Typography>
            </Box>
          )}
        </Stack>
        <Box display="flex" flexWrap="wrap" columnGap={1}>
          {infos?.map((info) => (
            <Typography variant="body2" key={info.slug}>
              {info.displayName}:
              <span style={{ fontWeight: 600 }}> {info.value}</span>
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <StyledListItem
      innerRef={listItemRef}
      button={assetEnable}
      // component="li"
      role="result-item"
      enable={assetEnable}
      sx={isFeatured ? { backgroundColor: '#F9F9F9' } : {}}
    >
      {comparisonMode ? (
        <Box
          width="100%"
          paddingX="16px"
          paddingY="6px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          onClick={handleAssetsToComparison}
          sx={{
            cursor: asset.enable && 'pointer',
            opacity: !asset.enable && 0.4,
            backgroundColor: !asset.enable && '#00000005',
          }}
        >
          {renderContent()}

          <Box display={{ xs: 'none', sm: 'block' }}>
            {assetEnable === undefined ? (
              assets.find((item) => item.identifier === asset.identifier) ? (
                <Button variant="contained" color="error" size="small">
                  Remover
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  id={asset.nicename}
                >
                  Adicionar
                </Button>
              )
            ) : (
              <Button disabled color="inherit" variant="contained" size="small">
                Ativo sem cota
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Link
          {...(linkProps as any)}
          href={asset.url}
          prefetch={false}
          onClick={handleClickLink}
          id={asset.identifier}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ pt: isFeatured && 2 }}
          >
            {renderContent()}
          </Box>
        </Link>
      )}
    </StyledListItem>
  );
};
